import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  MessageSquare,
  CheckCircle2,
  ShieldCheck,
  Send,
} from 'lucide-react';
import { api } from '../../../../shared/api/api.js';
import { useNotificationStore } from '../../../notifications/store/useNotificationStore.js';
import { useAuthStore } from '../../../auth/store/useAuthStore.js';

interface AskAuthorModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  postTitle?: string;
  authorName?: string;
}

export const AskAuthorModal: React.FC<AskAuthorModalProps> = ({
  isOpen,
  onClose,
  postId,
  postTitle,
  authorName,
}) => {
  const { user: authUser } = useAuthStore();
  const { showToast } = useNotificationStore();

  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSenderName(authUser?.name || '');
      setSenderEmail(authUser?.email || '');
      setQuestion('');
      setIsSubmittedSuccessfully(false);
    }
  }, [isOpen, authUser]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSendQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!senderName.trim() || !senderEmail.trim() || !question.trim()) {
      showToast('Galat Form', 'Semua kolom formulir wajib diisi.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.askAuthor(postId, senderName, senderEmail, question);
      setIsSubmittedSuccessfully(true);
      showToast('Pertanyaan Dikirim', 'Pertanyaan Anda berhasil diteruskan kepada penulis.', 'success');
    } catch (err: any) {
      showToast('Gagal Mengirim', err.message || 'Sistem gagal meneruskan pertanyaan Anda.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="bg-white rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl relative z-10 border border-slate-100 animate-in fade-in zoom-in duration-250 flex flex-col max-h-[90vh]">
        {/* Header Gradient */}
        <div className="bg-linear-to-r from-indigo-600 via-indigo-500 to-sky-400 p-8 text-white relative shrink-0 text-left">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-6 right-6 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all cursor-pointer"
            aria-label="Tutup"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center shadow-inner">
              <MessageSquare className="w-5 h-5 text-indigo-100" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest text-indigo-100">
              Konsultasi Privat
            </span>
          </div>
          <h3 className="text-2xl font-black tracking-tight leading-snug">Tanya Penulis Buku</h3>
          <p className="text-xs text-indigo-100/90 font-medium mt-1">
            Kirimkan pertanyaan privat Anda langsung ke dasbor penulis artikel ini.
          </p>
        </div>

        {/* Content Body */}
        <div className="p-8 overflow-y-auto space-y-6 text-left flex-1">
          {isSubmittedSuccessfully ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-3xl bg-emerald-50 text-emerald-500 flex items-center justify-center mx-auto shadow-inner shadow-emerald-100 border border-emerald-100">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-lg font-black text-slate-950">Pertanyaan Berhasil Dikirim!</h4>
                <p className="text-xs text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed">
                  Pertanyaan Anda telah diteruskan sebagai <b>Notifikasi Internal</b> ke penulis{' '}
                  <b>{authorName}</b> dan salinan surat elektronik privat berformat rapi telah dikirimkan.
                  Penulis akan membalas langsung ke alamat email Anda.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-4 text-xs font-medium text-slate-500 max-w-md mx-auto text-left border border-slate-100 space-y-1">
                <p className="font-bold text-slate-700">Ringkasan Pengiriman:</p>
                <p>• <b>Pengirim:</b> {senderName} ({senderEmail})</p>
                <p>• <b>Artikel:</b> {postTitle}</p>
                <p className="italic mt-1 text-slate-400">"{question}"</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-6 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-md shadow-indigo-100 cursor-pointer"
              >
                Selesai
              </button>
            </div>
          ) : (
            <form onSubmit={handleSendQuestion} className="space-y-5">
              {/* Article Context Mini Card */}
              <div className="p-4 rounded-2xl bg-indigo-50/50 border border-indigo-100/30 text-xs">
                <p className="text-[10px] font-black uppercase text-indigo-600 tracking-wider">
                  Artikel Referensi
                </p>
                <p className="font-bold text-slate-800 line-clamp-1 mt-0.5">{postTitle}</p>
                <p className="text-slate-400 mt-0.5">
                  Penulis: <span className="font-bold text-slate-600">{authorName}</span>
                </p>
              </div>

              {/* Sender Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                    Nama Lengkap
                  </label>
                  <input
                    type="text"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Nama Anda"
                    required
                    className="w-full px-4 py-2.5 text-xs rounded-xl bg-slate-50 border border-slate-100 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-bold text-slate-800"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                    Alamat Email
                  </label>
                  <input
                    type="email"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    placeholder="nama@domain.com"
                    required
                    className="w-full px-4 py-2.5 text-xs rounded-xl bg-slate-50 border border-slate-100 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-bold text-slate-800"
                  />
                </div>
              </div>

              {/* Question textarea */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                  Pertanyaan Anda
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ketikkan apa yang ingin Anda tanyakan atau diskusikan mengenai artikel ini kepada penulis..."
                  required
                  rows={4}
                  className="w-full px-4 py-3 text-xs rounded-2xl bg-slate-50 border border-slate-100 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-slate-700 leading-relaxed resize-none"
                />
              </div>

              {/* Privacy details */}
              <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 text-[10px] text-slate-400 border border-slate-100 font-medium">
                <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                  <b>Perlindungan Privasi:</b> Alamat email Anda hanya digunakan agar penulis dapat membalas pertanyaan ini secara langsung. Pesan dikirimkan secara privat dan tidak dipublikasikan di situs umum.
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 text-xs font-black uppercase tracking-wider rounded-xl border border-slate-200 hover:bg-slate-50 transition-all text-slate-500 text-center cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Mengirim...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>Kirim Pertanyaan</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
